import React, {useEffect, useState} from 'react';
import {Form, Input, Select, TimePicker} from 'antd';
import { passwordPolicyRule } from 'utils/passwordValidator';
import EditableDrawer from 'components/EditableDrawer';
import {FormInstance} from 'antd/es/form';
import {CountryCodeAndPhoneNumber, PhoneInput} from 'components/PhoneInput';
import {useWarehouseOptions} from 'hooks/useWarehouseOptions';
import {useWarehousePickupZones} from 'hooks/useWarehousePickupZones';
import {useVehicleOptions} from 'hooks/useVehicleOptions';
import {SiteType} from 'graphql/types/SiteType';
import {ROLE_OPTIONS} from 'constants/roleOptions';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import dayjs from 'dayjs';
import {AccountFormData} from 'hooks/useAccountApis';
import { useTranslation } from 'react-i18next';

interface AccountFormDrawerProps {
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: AccountFormData) => Promise<void>;
    account?: AccountFormData | null; // If account data is provided, it's edit mode, otherwise it's create mode
    mode: 'create' | 'edit'; // Explicitly specify mode
}

const AccountFormDrawer: React.FC<AccountFormDrawerProps> = ({
                                                                 visible,
                                                                 onClose,
                                                                 onSubmit,
                                                                 account,
                                                                 mode
                                                             }) => {
    const { t } = useTranslation();
    const drawerTitle = mode === 'create' ? t('pickup.account.pageDetails.newPersonnel') : t('pickup.account.pageDetails.personnelDetails');
    const [form] = Form.useForm();
    const { currentWarehouse: warehouse } = useWarehouseContext();

    useEffect(() => {
        if (account) {
            form.resetFields();
            // 确保 responsibleZipcodes 字段总是有值，并处理时间字段转换
            const accountWithDefaults = {
                ...account,
                responsibleZipcodes: account.responsibleZipcodes || [],
                // 将字符串时间转换为 Dayjs 时间范围
                workHours: account.workStartTime && account.workEndTime ?
                    [dayjs(account.workStartTime, 'HH:mm'), dayjs(account.workEndTime, 'HH:mm')] :
                    [dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')]
            };
            form.setFieldsValue(accountWithDefaults);
            form.setFieldsValue({
                role: account.role,
                warehouseId: warehouse?.id,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                role: 'PICKUP_DRIVER',
                status: 1,
                isOnDuty: true,
                warehouseId: warehouse?.id,
                phone: {
                    countryCode: 1,
                    phoneNumber: '',
                },
                responsibleZipcodes: [],
                workHours: [dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')],
            });
        }
    }, [account, form, visible, warehouse]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const values = await form.validateFields();

            // 将时间范围转换为独立的开始和结束时间
            const processedValues = {
                ...values,
                workStartTime: values.workHours && values.workHours[0] ?
                    values.workHours[0].format('HH:mm') : '09:00',
                workEndTime: values.workHours && values.workHours[1] ?
                    values.workHours[1].format('HH:mm') : '18:00'
            };

            await onSubmit(processedValues);
        } catch (error) {
            console.error('error', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditableDrawer
            title={drawerTitle}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            confirmText={isSubmitting ? t('pickup.account.messages.processing') as string : t('pickup.account.pageDetails.confirm') as string}
            cancelText={t('pickup.account.pageDetails.cancel') as string}
            confirmDisabled={isSubmitting}
            width={600}
        >
            <AccountForm
                form={form}
                mode={mode}
            />
        </EditableDrawer>
    );
};

interface AccountFormProps {
    mode: 'create' | 'edit';
    form: FormInstance<AccountFormData>;
}

const AccountForm = ({mode, form}: AccountFormProps) => {
    const { t } = useTranslation();

    const STATUS_OPTIONS = [
        {value: 1, label: t('enum.activeStatus.active')},
        {value: 0, label: t('enum.activeStatus.suspended')}
    ];
    const onFinish = (values: any) => {
        console.log(values);
    }

    const PickupDriverOptions = () => {
        let {warehouseOptions, loading} = useWarehouseOptions(SiteType.HUB);
        // 监听仓库选择变化
        const warehouseId = Form.useWatch('warehouseId', form);
        const {pickupZones, loading: pickupZonesLoading} = useWarehousePickupZones(warehouseId);
        const {vehicleOptions, loading: vehicleLoading} = useVehicleOptions(warehouseId);

        // 将pickup zones转换为选项格式
        const pickupZoneOptions = pickupZones.map(zipcode => ({
            value: zipcode,
            label: zipcode
        }));

        return (
            <>
                <Form.Item label={t('pickup.account.pageDetails.hub')} name="warehouseId"
                           rules={[{required: true, message: t('pickup.account.messages.pleaseSelectWarehouse') as string}]}>
                    <Select
                        disabled={true}
                        loading={loading}
                        options={warehouseOptions.map(w => ({value: w.id, label: w.name}))}
                        placeholder="select"
                    />
                </Form.Item>
                <Form.Item
                    label={t('pickup.account.pageDetails.pickupRange')}
                    name="responsibleZipcodes"
                    initialValue={[]}
                    rules={[{required: true, message: t('pickup.account.messages.pleaseSelectPickupRange') as string}]}
                >
                    <Select
                        mode="multiple"
                        loading={pickupZonesLoading}
                        placeholder={warehouseId ? t('pickup.account.placeholders.selectFromAvailablePickupZones') as string : t('pickup.account.placeholders.pleaseSelectWarehouseFirst') as string}
                        disabled={!warehouseId || pickupZones.length === 0}
                        options={pickupZoneOptions}
                        style={{width: '100%'}}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Form.Item>
                <Form.Item
                    label={t('pickup.account.pageDetails.workWeekdays')}
                    name="workWeekdays"
                    initialValue={[1, 2, 3, 4, 5]}
                    rules={[{required: true, message: t('pickup.account.messages.pleaseSelectWorkWeekdays') as string}]}
                >
                    <Select
                        mode="multiple"
                        placeholder={t('pickup.account.placeholders.selectWorkDays') as string}
                        options={[
                            {value: 1, label: t('enum.weekdaysOptions.monday')},
                            {value: 2, label: t('enum.weekdaysOptions.tuesday')},
                            {value: 3, label: t('enum.weekdaysOptions.wednesday')},
                            {value: 4, label: t('enum.weekdaysOptions.thursday')},
                            {value: 5, label: t('enum.weekdaysOptions.friday')},
                            {value: 6, label: t('enum.weekdaysOptions.saturday')},
                            {value: 7, label: t('enum.weekdaysOptions.sunday')}
                        ]}
                    />
                </Form.Item>
                <Form.Item
                    label={t('pickup.account.pageDetails.workHours')}
                    name="workHours"
                    initialValue={[dayjs('09:00', 'HH:mm'), dayjs('18:00', 'HH:mm')]}
                    rules={[{required: true, message: t('pickup.account.messages.pleaseSetWorkHours') as string}]}
                >
                    <TimePicker.RangePicker
                        format="HH:mm"
                        placeholder={[t('pickup.account.placeholders.startTime') as string, t('pickup.account.placeholders.endTime') as string]}
                        style={{width: '100%'}}
                    />
                </Form.Item>
                <Form.Item label={t('pickup.account.pageDetails.vehicle')} name="vehicleId">
                    <Select
                        loading={vehicleLoading}
                        placeholder={warehouseId ? t('pickup.account.placeholders.selectVehicleOptional') as string : t('pickup.account.placeholders.pleaseSelectWarehouseFirst') as string}
                        disabled={!warehouseId || vehicleOptions.length === 0}
                        allowClear
                        options={vehicleOptions}
                    />
                </Form.Item>
            </>
        )
    };

    const role = Form.useWatch('role', form);

    return (
        <Form
            form={form}
            onFinish={onFinish}
            labelCol={{span: 6}}
            wrapperCol={{span: 18}}
        >
            {mode === 'edit' && (
                <Form.Item label="id" name="id">
                    <Input placeholder={t('pickup.account.placeholders.inputPlaceholder') as string} disabled/>
                </Form.Item>
            )}
            <Form.Item label={t('pickup.account.pageDetails.phone')}
                       name="phone"
                       rules={[
                           {
                               validator: (_, value: CountryCodeAndPhoneNumber) => {
                                   if (!value || !value.phoneNumber) {
                                       return Promise.reject(new Error(t('pickup.account.messages.phoneNumberRequired') as string));
                                   }

                                   // 验证手机号只包含数字
                                   const phoneRegex = /^\d+$/;
                                   if (!phoneRegex.test(value.phoneNumber)) {
                                       return Promise.reject(new Error(t('pickup.account.messages.phoneNumberDigitsOnly') as string));
                                   }

                                   return Promise.resolve();
                               }
                           }
                       ]}
            >
                <PhoneInput/>
            </Form.Item>
            <Form.Item
                label={t('pickup.account.pageDetails.password')}
                name="password"
                validateTrigger="onBlur"
                rules={mode === 'create'
                    ? [{required: true, message: t('pickup.account.messages.pleaseInputPassword') as string}, passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]
                    : [passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]}
            >
                <Input.Password placeholder={t('accountSecurity.passwordHint') as string}/>
            </Form.Item>
            <Form.Item
                label={t('pickup.account.pageDetails.name')}
                name="name"
                rules={[{required: true, message: t('pickup.account.messages.pleaseInputName') as string}]}
            >
                <Input placeholder={t('pickup.account.placeholders.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item
                label={t('pickup.account.pageDetails.email')}
                name="email"
                rules={[{type: 'email', message: t('pickup.account.messages.pleaseInputValidEmail') as string}]}
            >
                <Input placeholder={t('pickup.account.placeholders.inputPlaceholder') as string}/>
            </Form.Item>
            <Form.Item label={t('pickup.account.pageDetails.personnelStatus')} name="status">
                <Select
                    options={STATUS_OPTIONS}
                    placeholder={t('pickup.account.pageDetails.select') as string}
                />
            </Form.Item>
            <Form.Item
                label={t('pickup.account.pageDetails.role')}
                name="role"
                rules={[{required: true, message: t('pickup.account.messages.pleaseSelectRole') as string}]}
            >
                <Select
                    disabled={true}
                    options={ROLE_OPTIONS}
                    placeholder="select"
                />
            </Form.Item>
            {role === 'PICKUP_DRIVER' && <PickupDriverOptions/>}
        </Form>
    )
};

export default AccountFormDrawer; 